import * as React from 'react';
import cx from 'classnames';
import { uniqBy } from 'lodash';
import moment from 'moment-timezone';

import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Coin, { CoinColor } from 'components/Coin';
import Grid from 'components/Grid';
import Heading from 'components/Heading';
import Link from 'components/Link';
import Row from 'components/Row';
import SelectBox from 'components/SelectBox';
import Text, { TextColor, TextWeight } from 'components/Text';
import TimezoneHelper from 'lib/timezones';

import './styles/DateTimeAvailabilitySelect.scss';
import { CoachingSessionSlot } from 'coaching/containers/GetPossibleCoachingSessionsForCoachContainer';

export type Props = {
  // the default visible date
  currentlyViewedDay?: moment.Moment;
  // the selected datetime option
  value?: CoachingSessionSlot;
  // callback for when a user selects a datetime option
  onChange?: (selectedSlot: CoachingSessionSlot) => void;
  // the specific datetime options that are available for the user
  // to select
  dateTimeOptions: CoachingSessionSlot[];
  // the range of days to show at the top of the select
  daysToShow: moment.Moment[];
  // the maximum number of days to show in a row
  daysPerRow: number;
  // the maximum number of timeslots to show in a row
  slotsPerRow: number;
  // callback for when a user selects a day
  onSelectDay?: (day: moment.Moment) => void;
  // callback for when a user expands availabilities
  onMoreAvailabilities?: () => void;
  // callback for when a user collapses availabilities
  onLessAvailabilities?: () => void;
  // callback for when a day is empty
  onEmptyDay?: (day: string) => void;
  // empty state text when no availabilities exist
  emptyText?: React.ReactNode;
  // render inside of a box with padding?
  box?: boolean;
  // timezone
  timezone?: string;
};

type State = {
  currentlyViewedDay?: moment.Moment;
  showingAdditionalAvailabilities: boolean;
};

class DateTimeAvailabilitySelect extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      currentlyViewedDay: props.currentlyViewedDay || props.daysToShow[0],
      showingAdditionalAvailabilities: false,
    };
  }

  componentDidUpdate() {
    const newDays = this.props.daysToShow;
    if (
      this.state.currentlyViewedDay &&
      !newDays.some(day => day.isSame(this.state.currentlyViewedDay, 'day'))
    ) {
      // if the daysToShow change, we should adjust the selected display day
      this.handleSelectDisplayDay(newDays[0]);
    }
  }

  handleSelectDateOption = (option: CoachingSessionSlot) => {
    const { onChange } = this.props;
    if (onChange) {
      onChange(option);
    }
  };

  handleSelectDisplayDay = (day: moment.Moment) => {
    if (this.props.onSelectDay) {
      this.props.onSelectDay(day);
    }
    this.setState({ currentlyViewedDay: day });
  };

  datesAreSame = (day1: moment.Moment, day2: moment.Moment) => {
    return day1.date() === day2.date() && day1.month() === day2.month();
  };

  getDateTimeOptionsForDisplayDay = (day: moment.Moment) => {
    return this.props.dateTimeOptions.filter(option =>
      this.datesAreSame(option.startDateTime, day)
    );
  };

  renderDisplayDay = (
    dayOfWeekLabel: string,
    dateOfMonthLabel: string,
    dayIsSelected: boolean,
    nextDayIsNextWeek: boolean,
    dayHasNoOptions: boolean,
    onClick: () => void
  ) => {
    const coinAndTextStyles = (function(): {
      coinColor: CoinColor;
      coinBorderColor: CoinColor;
      textColor: TextColor;
      textWeight: TextWeight;
      subtle: boolean;
    } {
      if (dayIsSelected) {
        return {
          coinColor: 'mascot',
          coinBorderColor: 'mascot',
          textColor: 'chalkboard',
          textWeight: 'semibold',
          subtle: true,
        };
      } else if (dayHasNoOptions) {
        return {
          coinColor: 'paper',
          coinBorderColor: 'percussion',
          textColor: 'percussion',
          textWeight: 'normal',
          subtle: false,
        };
      } else {
        return {
          coinColor: 'whiteout',
          coinBorderColor: 'whiteout',
          textColor: 'graphite',
          textWeight: 'normal',
          subtle: false,
        };
      }
    })();

    return (
      <div
        key={`${dayOfWeekLabel}-${dateOfMonthLabel}`}
        className={cx(
          'DateTimeAvailabilitySelect-dayItem',
          nextDayIsNextWeek && 'DateTimeAvailabilitySelect-dayItem-endOfWeek'
        )}>
        <Link onClick={onClick} data-test="display-day-list-item">
          <Text align="center">
            <Row size="xxsmall">
              <Text size="xxxsmall" color="graphite">
                {dayOfWeekLabel}
              </Text>
            </Row>
            <Coin
              className={cx(
                'DateTimeAvailabilitySelect-dayItem-coin',
                dayHasNoOptions && 'DateTimeAvailabilitySelect-dayItem-coin--strikethrough'
              )}
              color={coinAndTextStyles.coinColor}
              subtle={coinAndTextStyles.subtle}
              border={coinAndTextStyles.coinBorderColor}
              size="xxxlarge"
              thick>
              <Text
                size="xsmall"
                color={coinAndTextStyles.textColor}
                weight={coinAndTextStyles.textWeight}>
                {dateOfMonthLabel}
              </Text>
            </Coin>
          </Text>
        </Link>
      </div>
    );
  };

  renderDaysToShow = () => {
    const { daysPerRow, daysToShow } = this.props;
    const uniqueDaysToShow = uniqBy(daysToShow, day => day.date()).slice(0, daysPerRow);
    const { currentlyViewedDay } = this.state;
    if (!currentlyViewedDay) {
      return null;
    }

    return (
      <Grid columns={Math.min(daysPerRow, uniqueDaysToShow.length)}>
        {uniqueDaysToShow.map((day, index, days) => {
          const nextDay = days[index + 1];
          const nextDayIsNextWeek = nextDay && nextDay.isAfter(day, 'week');
          return this.renderDisplayDay(
            day.format('ddd'),
            day.format('D'),
            this.datesAreSame(day, currentlyViewedDay),
            nextDayIsNextWeek,
            this.getDateTimeOptionsForDisplayDay(day).length === 0,
            () => this.handleSelectDisplayDay(day)
          );
        })}
      </Grid>
    );
  };

  renderDateTimeOptions = () => {
    const { currentlyViewedDay } = this.state;
    if (!currentlyViewedDay) {
      return null;
    }

    const options = this.getDateTimeOptionsForDisplayDay(currentlyViewedDay);
    if (options.length === 0) {
      if (this.props.onEmptyDay) {
        this.props.onEmptyDay(currentlyViewedDay.format('ddd, MMM D'));
      }
      return <Text color="graphite">No availability. Try changing the date.</Text>;
    }

    const { onLessAvailabilities, onMoreAvailabilities, slotsPerRow } = this.props;
    const { showingAdditionalAvailabilities } = this.state;
    const linkText = showingAdditionalAvailabilities ? 'Show less' : '+ Show more availability';
    const availabilityCap = showingAdditionalAvailabilities ? undefined : slotsPerRow;

    return (
      <React.Fragment>
        <Row size="xsmall">
          <Grid columns={slotsPerRow} gap="xxsmall">
            {options.slice(0, availabilityCap).map(option => {
              const optionIsSelected = option.startDateTime.isSame(
                this.props.value?.startDateTime,
                'second'
              );
              return (
                <SelectBox
                  key={option.startDateTime.toISOString()}
                  data-test="date-time-option"
                  onSelect={() => this.handleSelectDateOption(option)}
                  selected={optionIsSelected}
                  label={option.startDateTime.format('LT')}
                  textSize="xsmall"
                  full
                  flush
                />
              );
            })}
          </Grid>
        </Row>
        {options.length > slotsPerRow && (
          <Link
            data-test="toggle-availability-count-link"
            color="chalkboard"
            onClick={() => {
              if (showingAdditionalAvailabilities && onLessAvailabilities) {
                // if we are already showing additional availabilities, trigger
                // callback for collapsing
                onLessAvailabilities();
              } else if (!showingAdditionalAvailabilities && onMoreAvailabilities) {
                // if we are not showing additional availabilities, trigger
                // callback for expanding
                onMoreAvailabilities();
              }
              this.setState({ showingAdditionalAvailabilities: !showingAdditionalAvailabilities });
            }}>
            {linkText}
          </Link>
        )}
      </React.Fragment>
    );
  };

  render() {
    const timezone = this.props.timezone ?? moment.tz.guess();
    const renderComponent = this.state.currentlyViewedDay ? (
      <>
        <Row>{this.renderDaysToShow()}</Row>
        <Text align="left">
          <Row size="xsmall">
            <Heading data-test="heading-viewed-day" level="5" flush>
              {this.state.currentlyViewedDay.format('ddd, MMM D')}
            </Heading>
            <Text size="xxsmall" color="graphite">
              Times shown in {TimezoneHelper.convertToReadableShortCode(timezone)}
            </Text>
          </Row>
          {this.renderDateTimeOptions()}
        </Text>
      </>
    ) : (
      <Heading level="4" flush>
        {this.props.emptyText ??
          "Sorry, there are currently no coaches available for the options you've selected."}
      </Heading>
    );

    if (!this.props.box) {
      return renderComponent;
    }

    return (
      <Box noShadow className="DateTimeAvailabilitySelect">
        <BoxBody size="small">{renderComponent}</BoxBody>
      </Box>
    );
  }
}

export default DateTimeAvailabilitySelect;
